import {
  Button, Card, DatePicker, Empty, Form, Input, InputNumber, Select, Space, Typography, message, Segmented, Pagination,
} from "antd";
import {
  DownOutlined, FilePdfOutlined, FileTextOutlined, PlayCircleOutlined, ReloadOutlined, UpOutlined, LeftOutlined, RightOutlined,
  SettingOutlined, AppstoreOutlined, ProfileOutlined, HomeOutlined, CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import http from "@/utils/http";
import api from "@/utils/api";
import ReportCreator from "./ReportCreator";
import "./index.css";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const money = (value) => {
  const amount = Number(value || 0);
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: 2,
  }).format(Math.abs(amount));
  return amount < 0 ? `(${formatted})` : formatted;
};
const asList = (response) => Array.isArray(response) ? response : (response?.data || []);
// Keep status out of identifiers, including responses from an older server during deployment.
const propertyCode = (property) => property?.propCode || (property?.prop || "").replace(/\s*\([PX]\)\s*$/i, "");
const propertyStatus = (property) => property?.propertyStatus || (/\(P\)\s*$/i.test(property?.prop || "") ? "Active" : /\(X\)\s*$/i.test(property?.prop || "") ? "Inactive" : "—");
const initialValues = () => ({
  dateRange: [dayjs().startOf("year"), dayjs()], prop: "", trustIdList: [],
});

/** Java replacement for the PHP Accounting Report 8825 horizontal report. */
const Accounting8825Report = () => {
  const [form] = Form.useForm();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [trustOptions, setTrustOptions] = useState([]);
  const [viewMode, setViewMode] = useState("separate");
  const [selectedPropId, setSelectedPropId] = useState(null);
  const [propertyPage, setPropertyPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const tableRef = useRef(null);
  const trustsLoaded = useRef(false);

  useEffect(() => { form.setFieldsValue(initialValues()); }, [form]);

  const loadTrusts = async () => {
    if (trustsLoaded.current) return;
    try {
      const options = asList(await http.get(api.getTrustDictList))
        .map((trust) => ({ value: trust.trustId, label: trust.trustNameTitle || trust.trust }))
        .filter((option) => option.value !== undefined && option.value !== null);
      setTrustOptions(options);
      trustsLoaded.current = true;
    } catch (_error) {
      message.warning("Trust options could not be loaded.");
    }
  };

  const payload = async () => {
    const values = await form.validateFields();
    const [dateFrom, dateTo] = values.dateRange || [];
    return {
      dateFrom: dateFrom?.format("YYYY-MM-DD"),
      dateTo: dateTo?.format("YYYY-MM-DD"),
      prop: values.prop?.trim() || "",
      trustIdList: values.trustIdList || [],
    };
  };

  const reset = () => {
    form.setFieldsValue(initialValues());
    setReport(null);
  };

  const run = async () => {
    setLoading(true);
    try {
      const response = await http.post(api.getAccounting8825Show, await payload());
      if (response?.code !== "1") throw new Error(response?.msg || "Unable to run report 8825.");
      setReport(response.data);
      setSelectedPropId(response.data?.columns?.[0]?.propId ?? null);
      setPropertyPage(1);
    } catch (error) {
      message.error(error?.message || "Please review the report filters.");
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    setExporting(format);
    try {
      const endpoint = format === "pdf" ? api.getAccounting8825ToPdf : api.getAccounting8825ToCsv;
      const blob = await http.post(endpoint, await payload(), { responseType: "blob" });
      if (!(blob instanceof Blob)) throw new Error("Invalid file response");
      if (blob.type.includes("application/json")) {
        throw new Error((JSON.parse(await blob.text()))?.msg || "Export failed.");
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Accounting_8825_${dayjs().format("YYYYMMDD_HHmmss")}.${format}`;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      message.error(error?.message || "Export failed.");
    } finally {
      setExporting("");
    }
  };

  const properties = useMemo(() => report?.columns || [], [report]);
  const selectedIndex = Math.max(0, properties.findIndex((property) => property.propId === selectedPropId));
  const selectedProperty = properties[selectedIndex];
  // Limit the DOM width for large reports; every property remains accessible through the column pager.
  const visibleProperties = viewMode === "separate" ? (selectedProperty ? [selectedProperty] : [])
    : properties.slice((propertyPage - 1) * pageSize, propertyPage * pageSize);
  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollTo({ top: 0, left: 0 });
  }, [viewMode, selectedPropId, propertyPage, pageSize, report]);

  const changePageSize = (size) => {
    if (!Number.isInteger(size) || size < 1 || size > 500) return;
    setPageSize(size);
    setPropertyPage(1);
  };

  return (
    <main className="accounting-8825-page">
      <section className={`accounting-8825-layout${filtersCollapsed ? " accounting-8825-layout-collapsed" : ""}`}>
          <Card className="accounting-8825-filter-card" style={filtersCollapsed ? { display: "none" } : undefined} title={(
            <div className="accounting-8825-filter-header">
              <div className="accounting-8825-title-row">
                <span>Report filters</span>
                <Button className="accounting-8825-collapse" type="text" icon={<UpOutlined />} onClick={() => setFiltersCollapsed(true)}>Hide filters</Button>
              </div>
              <div className="accounting-8825-actions">
                <Button icon={<ReloadOutlined />} onClick={reset}>Reset</Button>
                <Button type="primary" icon={<PlayCircleOutlined />} loading={loading} onClick={run}>Run Report</Button>
              </div>
            </div>
          )}>
            <Form form={form} layout="vertical">
              <Form.Item name="dateRange" label="Date Range" rules={[{ required: true, message: "Select a date range" }]}>
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="prop" label="Property">
                <Input.TextArea className="accounting-8825-property-input" rows={3} placeholder="e.g. 0001, 1005-1010" />
              </Form.Item>
              <Form.Item name="trustIdList" label="Trust">
                <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={trustOptions} onDropdownVisibleChange={(open) => open && loadTrusts()} />
              </Form.Item>
            </Form>
            <div className="accounting-8825-creator-link"><div className="accounting-8825-creator-heading"><SettingOutlined /><strong>Report Creator</strong></div><p>Groups, GL ranges and display order.</p><Button block onClick={() => setCreatorOpen(true)}>Configure rules <RightOutlined /></Button></div>
          </Card>
        <div className="accounting-8825-preview-column">
          {filtersCollapsed && <div className="accounting-8825-show-filters"><Button className="accounting-8825-collapse" type="text" icon={<DownOutlined />} onClick={() => setFiltersCollapsed(false)}>Show filters</Button></div>}
          <Card className="accounting-8825-result-card" title={<div className="accounting-8825-preview-title"><span className="accounting-8825-report-mark"><FileTextOutlined /></span><div>Report preview<small>8825 · Rental real estate</small></div></div>} extra={(
            <Space>
              {filtersCollapsed && <Button icon={<SettingOutlined />} onClick={() => setCreatorOpen(true)}>Rules</Button>}
              <Button icon={<FileTextOutlined />} loading={exporting === "csv"} onClick={() => download("csv")}>CSV</Button>
              <Button type="primary" icon={<FilePdfOutlined />} loading={exporting === "pdf"} onClick={() => download("pdf")}>PDF</Button>
            </Space>
          )}>
            {report ? <>
              <div className="accounting-8825-summary">
                <div className="accounting-8825-period"><span className="accounting-8825-property-count">{properties.length} properties</span><span><CalendarOutlined /> {dayjs(report.dateFrom).format("MMM D, YYYY")} – {dayjs(report.dateTo).format("MMM D, YYYY")}</span></div>
                <Segmented aria-label="Property display" value={viewMode} onChange={setViewMode} options={[{ label: "Combined", icon: <AppstoreOutlined />, value: "combined" }, { label: "By property", icon: <ProfileOutlined />, value: "separate" }]} />
              </div>
              <div className={`accounting-8825-report-surface accounting-8825-mode-${viewMode}`}>
              {viewMode === "separate" && selectedProperty && <>
                <div className="accounting-8825-property-nav">
                  <span className="accounting-8825-nav-label">PROPERTY</span>
                  <Select aria-label="Select property" showSearch optionFilterProp="label" value={selectedProperty.propId} onChange={setSelectedPropId} options={properties.map((p) => ({ value: p.propId, label: `${propertyCode(p)} · ${p.address || ""}` }))} />
                  <Text className="accounting-8825-nav-counter" type="secondary">{selectedIndex + 1} of {properties.length}</Text>
                  <Button aria-label="Previous property" icon={<LeftOutlined />} disabled={selectedIndex === 0} onClick={() => setSelectedPropId(properties[selectedIndex - 1].propId)} />
                  <Button aria-label="Next property" icon={<RightOutlined />} disabled={selectedIndex === properties.length - 1} onClick={() => setSelectedPropId(properties[selectedIndex + 1].propId)} />
                </div>
                <div className="accounting-8825-property-detail">
                  <div className="accounting-8825-property-identity"><span className="accounting-8825-property-icon"><HomeOutlined /></span><div><strong>{propertyCode(selectedProperty)}<span>{selectedProperty.address}</span></strong><p>{selectedProperty.cityStateZip}</p></div></div>
                  <div className="accounting-8825-property-facts"><div><span>Status</span><strong>{propertyStatus(selectedProperty)}</strong></div><div><span>Property type</span><strong>{selectedProperty.propType || "—"}</strong></div><div><span>Units</span><strong>{selectedProperty.units ?? "—"}</strong></div><div><span>Purchase date / price</span><strong>{selectedProperty.purchaseDatePrice || "—"}</strong></div></div>
                </div>
              </>}
              {properties.length ? <div ref={tableRef} className={`accounting-8825-table-scroll ${viewMode}`} tabIndex={0} aria-label="8825 report data" aria-busy={loading}>
                <table className="accounting-8825-data-table">
                  <thead><tr><th scope="col">Description</th>{visibleProperties.map((p) => <th key={p.propId} scope="col" className="accounting-8825-money-cell">
                    {viewMode === "combined" && <div className="accounting-8825-column-property">
                      <div className="accounting-8825-column-identity"><strong>{propertyCode(p)}</strong><span className="accounting-8825-column-status">{propertyStatus(p)}</span></div>
                      <div className="accounting-8825-column-address">{p.address || "—"}<span>{p.cityStateZip || "—"}</span></div>
                      <dl className="accounting-8825-column-facts"><div><dt>Property type</dt><dd>{p.propType || "—"}</dd></div><div><dt>Units</dt><dd>{p.units ?? "—"}</dd></div><div className="accounting-8825-column-purchase"><dt>Purchase date / price</dt><dd>{p.purchaseDatePrice || "—"}</dd></div></dl>
                    </div>}
                    <span className="accounting-8825-column-amount">Amount</span>
                  </th>)}{viewMode === "separate" && <th className="accounting-8825-reading-space" aria-hidden="true" />}</tr></thead>
                  <tbody>{(report.rows || []).map((row, index) => <tr key={`${row.rowType}-${index}`} className={`accounting-8825-row-${row.rowType.toLowerCase()}`}>
                    <th scope="row">{row.label}</th>{visibleProperties.map((p) => <td key={p.propId} className="accounting-8825-money-cell">{["GROUP", "SPACER"].includes(row.rowType) ? "" : money(row.amounts?.[p.propId])}</td>)}
                    {viewMode === "separate" && <td className="accounting-8825-reading-space" aria-hidden="true" />}
                  </tr>)}</tbody>
                </table>
              </div> : <Empty description="No properties found" />}
              </div>
              <div className="accounting-8825-preview-footer"><Text type="secondary">PDF: one property per page</Text>{viewMode === "combined" && <div className="accounting-8825-pagination-controls"><label htmlFor="accounting-8825-page-size">Properties per page</label><InputNumber id="accounting-8825-page-size" aria-label="Properties per page" size="small" min={1} max={500} precision={0} value={pageSize} onChange={changePageSize} title="1–500 properties per page" /><Pagination size="small" current={propertyPage} pageSize={pageSize} total={properties.length} showSizeChanger={false} onChange={setPropertyPage} showTotal={(total, range) => `Properties ${range[0]}–${range[1]} of ${total}`} /></div>}</div>
            </> : <Empty className="accounting-8825-empty" description="Run report 8825 to review data before exporting." />}
          </Card>
        </div>
      </section>
      <ReportCreator open={creatorOpen} onClose={() => setCreatorOpen(false)} onSaved={() => setReport(null)} />
    </main>
  );
};

export default Accounting8825Report;
